import FileAES from './fileAES';
import ByteEncoder from './byteEncoder';
import Base64Encoder from './base64encoder';
import Base = Mocha.reporters.Base;
import SecureRandom from '@/util/SecureRandom';

export default class KeyUtility {

    private static keyFormat: string = 'raw';
    private static keyDerivationRounds: number = 25000;
    private static keyDerivationHash: string = 'SHA-512';

    public static generateRandomKey(): Promise<CryptoKey> {
        return FileAES.generateKey();
    }

    public static generateIV(): Uint8Array {
        return SecureRandom.generate(FileAES.ivLength);
    }

    public static generatePBKDFSalt(): Uint8Array {
        return SecureRandom.generate(16); //NIST Recommendation: 128bit salt
    }

    // The salt should at least be a 64 Bit random value
    public static async deriveKeyFromPassword(password: string, salt: Uint8Array): Promise<CryptoKey> {
        // First: Import the Password as key
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            ByteEncoder.stringToBytes(password),
            'PBKDF2',
            false,
            ['deriveBits', 'deriveKey'],
        );

        // Safari cannot derive a key directly so, we need to derive bits and then import them as a key.
        // according to MDN this is the same as deriveKey:
        // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/deriveBits

        const keyBits = await crypto.subtle.deriveBits({
                name: 'PBKDF2',
                salt,
                iterations: this.keyDerivationRounds,
                hash: this.keyDerivationHash,
            },
            keyMaterial,
            FileAES.keyLength * 8,
        );

        return crypto.subtle.importKey(
            this.keyFormat,
             keyBits,
            {name: FileAES.opMode, length: FileAES.keyLength},
            true,
            ['encrypt', 'decrypt'],
        );
    }

    public static async exportKeyToBase64(key: CryptoKey): Promise<string> {
        const bytes = await crypto.subtle.exportKey(this.keyFormat, key) as ArrayBuffer;
        const b64String = Base64Encoder.bytesToString(new Uint8Array(bytes));
        return Promise.resolve(b64String);
    }

    public static async loadKeyFromBase64(base64Input: string): Promise<CryptoKey> {
        const bytes = Base64Encoder.stringToBytes(base64Input);
        return crypto.subtle.importKey(
            this.keyFormat,
            bytes,
            {name: FileAES.opMode, length: FileAES.keyLength},
            true,
            ['encrypt', 'decrypt'],
        );
    }
}
